import { ReactNode, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, MessageSquare, UserPlus, Users } from 'lucide-react';
import AppBackButton from '../common/AppBackButton';
import './MessageHub.css';

export type HubTab = 'messages' | 'questions' | 'contacts' | 'requests';

const TABS: { tab: HubTab; path: string; label: string; icon: ReactNode }[] = [
  {
    tab: 'messages',
    path: '/messages',
    label: 'Messages',
    icon: <MessageSquare />,
  },
  {
    tab: 'questions',
    path: '/messages/questions',
    label: 'Questions',
    icon: <HelpCircle />,
  },
  {
    tab: 'contacts',
    path: '/messages/contacts',
    label: 'Contacts',
    icon: <Users />,
  },
  {
    tab: 'requests',
    path: '/messages/requests',
    label: 'Requests',
    icon: <UserPlus />,
  },
];

type MessageHubShellProps = {
  active: HubTab;
  mobileView: 'list' | 'detail';
  listColumn: ReactNode;
  detailColumn: ReactNode;
  /** Bumps when detail pane content changes so flex height recalculates on first paint. */
  layoutRevision?: string | number | null;
};

export function MessageHubShell({
  active,
  mobileView,
  listColumn,
  detailColumn,
  layoutRevision = null,
}: MessageHubShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return;

    const reflow = () => {
      const chain = [
        page.closest('.main-layout--message-hub'),
        page.closest('.content-container--message-hub'),
        page.closest('.main-content--message-hub'),
        page.closest('.tailwind-layout-shell'),
        page.closest('#tailwind-root'),
        page,
      ] as HTMLElement[];
      chain.forEach((el) => {
        if (el) void el.offsetHeight;
      });
    };

    reflow();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      reflow();
      raf2 = requestAnimationFrame(reflow);
    });

    const observer = new ResizeObserver(() => reflow());
    observer.observe(page);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer.disconnect();
    };
  }, [active, location.pathname, mobileView, layoutRevision]);

  const layoutClass =
    mobileView === 'detail' ? 'mh-layout mh-layout--detail' : 'mh-layout mh-layout--list';

  return (
    <div className="mh-page" ref={pageRef}>
      <div className="mh-page-back">
        <AppBackButton to="/dashboard" />
      </div>
      <header className="mh-hub-header mh-hub-header--compact">
        <p className="mh-hub-eyebrow">Recruiter communication</p>
        <p className="mh-hub-subtitle">Conversations, questions, contacts, and requests.</p>
        <nav className="mh-hub-tabs" aria-label="Messages hub sections">
          {TABS.map(({ tab, path, label, icon }) => {
            const isActive =
              tab === 'messages'
                ? location.pathname === '/messages'
                : location.pathname === path || location.pathname.startsWith(`${path}/`);
            return (
              <button
                key={tab}
                type="button"
                className={`mh-hub-tab ${isActive ? 'mh-hub-tab--active' : ''}`}
                onClick={() => navigate(path)}
              >
                {icon}
                {label}
              </button>
            );
          })}
        </nav>
      </header>

      <div className={layoutClass}>
        <aside className="mh-inbox-col">
          <div className="mh-inbox-inner">{listColumn}</div>
        </aside>
        <section className="mh-thread-col" aria-label="Conversation">
          {detailColumn}
        </section>
      </div>
    </div>
  );
}
